import { useCallback, useState } from 'react'
import Media, { getMediaType, MediaSize } from '~components/ui/media'

import { cn } from '~utils/cn'
import MediaModal from './media-modal'

type MediaListProps = Readonly<{
  photoUrls?: string[]
  videoUrls?: string[]
  size?: MediaSize
}>

const useMedia = ({ photoUrls, videoUrls }: MediaListProps) => {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null)

  const mediaUrls = videoUrls?.concat(photoUrls ?? []) || []
  const focusedMediaUrl = focusedIndex !== null ? mediaUrls[focusedIndex] : ''

  const onMediaClick = useCallback((index: number) => setFocusedIndex(index), [])

  const onPrevClick = useCallback(() => {
    setFocusedIndex(prev => {
      if (prev === null) {
        return prev
      }
      return prev > 0 ? prev - 1 : prev
    })
  }, [])

  const onNextClick = useCallback(() => {
    setFocusedIndex(prev => {
      if (prev === null) {
        return prev
      }
      return mediaUrls.length - 1 > prev ? prev + 1 : prev
    })
  }, [mediaUrls.length])

  const onRequestClose = useCallback(() => setFocusedIndex(null), [])

  return {
    mediaUrls,
    onMediaClick,
    mediaModalProps: {
      url: focusedMediaUrl,
      totalMediaCount: mediaUrls.length,
      focusedIndex,
      onPrevClick,
      onNextClick,
      onRequestClose,
    },
  }
}

const MediaList = ({ photoUrls, videoUrls, size = '96' }: MediaListProps) => {
  const { mediaUrls, onMediaClick, mediaModalProps } = useMedia({
    photoUrls,
    videoUrls,
  })

  return (
    <>
      <div className="no-scrollbar flex flex-gap-x-2 overflow-x-scroll">
        {mediaUrls.map((url, index) => (
          <Media
            key={index}
            url={url}
            alt="thumbnail"
            className={cn(getMediaType(url) === 'photo' && !!videoUrls?.length && 'last:mr-5')}
            onClick={() => onMediaClick(index)}
            size={size}
          />
        ))}
      </div>
      {mediaModalProps.focusedIndex !== null && <MediaModal {...mediaModalProps} />}
    </>
  )
}

export default MediaList
